import { Tooltip, tooltipClasses, useTheme, Box, Chip } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import { getDesignTokens } from '~/client/components/base/core';
import { GenericCardContent } from '~/client/components/base/generic-card';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';
import * as GQL from '~/graphql/generated/client';
import { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';

interface Props {
  className?: string;
  policy: GQL.PolicyFragment | GQL.ControlBundleFragment;
  variant?: 'default' | 'governance';
}

const PolicyCardContentImpl: React.FC<Props> = ({
  className,
  policy,
  variant = 'default',
}) => {
  const theme = useTheme();
  const tokens = useDesignTokens();

  // Type guards
  const isPolicy = (p: any): p is GQL.PolicyFragment =>
    Array.isArray(p.controlBundleList);
  const isControlBundle = (p: any): p is GQL.ControlBundleFragment =>
    Array.isArray(p.controls);

  if (isPolicy(policy)) {
    // Policy display (existing logic)
    const { name, description, controlBundleList, labels, isSystemLevel } =
      policy;

    const totalControls =
      controlBundleList?.reduce(
        (total, bundle) => total + (bundle.controls?.length || 0),
        0
      ) || 0;

    return (
      <GenericCardContent className={className}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Text sz="title-medium" className="policy-card-title" truncate>
            {name}
          </Text>
          {!isSystemLevel && (
            <Chip
              label="Private"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                backgroundColor: tokens.status.info,
                color: 'white',
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <div className="policy-card-spacer" />
        {variant === 'default' && (
          <Text sz="body12" color={theme.palette.text.secondary} truncate>
            <Tooltip title={description} placement="bottom" arrow>
              <span>{description}</span>
            </Tooltip>
          </Text>
        )}
        {variant === 'governance' && (
          <div className="policy-card-controls-section">
            <Text sz="body12" color={theme.palette.text.secondary}>
              {totalControls} Controls
            </Text>
            <Text sz="body12" color={theme.palette.text.secondary}>
              {controlBundleList?.length ?? 0} Control Bundles
            </Text>
          </div>
        )}
      </GenericCardContent>
    );
  } else if (isControlBundle(policy)) {
    // Control bundle display
    const { name, description, controls } = policy;
    return (
      <GenericCardContent className={className}>
        <Text
          sz="title-medium"
          className="policy-card-title"
          sx={{ mb: spc.xxs }}
          truncate
        >
          {name}
        </Text>
        {/* Add space below the name */}
        <div className="policy-card-spacer" />
        <Text sz="body12" color={theme.palette.text.secondary} truncate>
          <Tooltip title={description} placement="bottom" arrow>
            <span>{description}</span>
          </Tooltip>
        </Text>
        {/* Add space below the description */}
        <div className="policy-card-controls-spacer" />
        <div className="policy-card-controls-section">
          <Text sz="body12" color={theme.palette.text.secondary}>
            {controls?.length ?? 0} Controls
          </Text>
        </div>
      </GenericCardContent>
    );
  } else {
    // Fallback for unknown type
    return null;
  }
};

export const PolicyCardContent = muiStyled(PolicyCardContentImpl)(({
  theme,
}) => {
  const tokens = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    & .policy-card-title {
      & .MuiTypography-root {
        color: ${tokens.accent};
        text-transform: uppercase;
        letter-spacing: 0.1rem;
      }
    }

    & .${tooltipClasses.tooltip} {
      max-width: 250px;
      background-color: black;
      font-size: 15px;
    }

    & .${tooltipClasses.arrow} {
      color: black;
    }

    & .policy-card-spacer {
      margin-bottom: 12px;
    }

    & .policy-card-controls-spacer {
      margin-bottom: 16px;
    }

    & .policy-card-controls-section {
      padding-top: 8px;
    }
  `;
});

export default PolicyCardContent;
